import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { InputContainer } from '@creditornot/cb-form';
import { Input } from '@creditornot/cb-input';
import { Radio } from '@creditornot/cb-radio';
import { FORM_ERROR } from 'final-form';
import React, { useMemo, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { deleteMenuFile, patchInboundMerchant, uploadMenuFile } from 'apis/merchant-onboarding-service/api';
import { RFFFieldErrorWrapper } from 'common/components';
import { Typography as T } from 'common/typography';
import validateYupSchema from 'common/utils/validateYupSchema';
import { InboundMerchant, SsmoMenuFile } from 'modules/onboarding-page/types';

import { InboundMerchantUpdateRequest } from '../../../../apis/merchant-onboarding-service/types';
import {
  ssmoMenuUploadDataMenuUploadStatus,
  ssmoMenuUploadInteractionClickSave,
} from '../../../../core/telemetry/avo/Avo';
import { SsmoTask, SsmoStepsIdentifiers } from '../../constants';
import { prefillLinkWithHttps } from '../../utils';
import { DocumentUploader } from '../DocumentUploader/DocumentUploader';
import StepPageWrapper from '../StepPageWrapper';
import { ProductList, productListValidationSchema } from '../validation';

type UploadMenuFormProps = {
  inboundMerchant: InboundMerchant;
};

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-block-end: 32px;
  width: 100%;
`;

const StyledRadio = styled(Radio)`
  margin-block-end: 16px;
`;

const StyledDescription = styled(T.Body3)`
  margin-block-start: 8px;
  margin-block-end: 32px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
})`
  flex-grow: 1;
  margin-block-start: 56px;
`;

const createEmptyFileArrayFromMenuFiles = (menuFiles: SsmoMenuFile[]): File[] => {
  if (!menuFiles || menuFiles.length === 0) {
    return [];
  }

  const emptyMenuFiles = menuFiles.map(menuFile => {
    const buffer = new Uint8Array(menuFile.sizeInBytes).fill(0);
    const blob = new Blob([buffer], { type: 'text/plain' });
    const file = new File([blob], menuFile.name, { type: 'text/plain' });

    return file;
  });

  return emptyMenuFiles;
};

const UploadMenuForm = ({ inboundMerchant }: UploadMenuFormProps) => {
  const { formatMessage } = useIntl();
  const history = useHistory();

  const [uploadedFiles, setUploadedFiles] = useState<File[]>(
    createEmptyFileArrayFromMenuFiles(inboundMerchant.ssmoMenuFiles || []),
  );
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [failedToUpload, setFailedToUpload] = useState(false);
  const [fileBeingDeletedName, setFileBeingDeletedName] = useState<string | null>(null);

  const initialValues: ProductList = useMemo(() => {
    const { ssmoMenuSource, ssmoMenuFileLink } = inboundMerchant;
    return { source: ssmoMenuSource === 'MENU_LINK' ? 'link' : 'file', link: ssmoMenuFileLink || '' };
  }, [inboundMerchant]);

  const handleOnFileUpload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setIsUploadingFile(true);
      await uploadMenuFile(formData);

      setUploadedFiles([...uploadedFiles, file]);
      setFailedToUpload(false);
      ssmoMenuUploadDataMenuUploadStatus({
        fileType: file.type,
        menuUploadStatus: 'success',
        ssmoMenuType: 'menu file',
      });
    } catch (error) {
      setFailedToUpload(true);

      console.error('Error uploading file:', error);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleOnDeleteFile = async (file: File) => {
    const fileName = file.name;

    try {
      setFileBeingDeletedName(fileName);
      await deleteMenuFile(fileName);
      setFileBeingDeletedName(null);

      const newFiles = uploadedFiles.filter(uploadedFile => uploadedFile !== file);
      setUploadedFiles(newFiles);
    } catch (error) {
      setFileBeingDeletedName(null);
      console.error('Error deleting file:', error);
    }
  };

  const handleSubmitUploadMenu = async (values: ProductList) => {
    const isLinkSource = values.source === 'link';

    ssmoMenuUploadInteractionClickSave();

    let payload: InboundMerchantUpdateRequest;
    if (isLinkSource) {
      const validFormValues = productListValidationSchema.validateSync(values);
      payload = {
        ssmoMenuFileLink: validFormValues?.link ?? '',
        ssmoStepCompletionId: SsmoStepsIdentifiers.UploadMenu,
        ssmoMenuSource: 'MENU_LINK',
      };
    } else {
      payload = {
        ssmoStepCompletionId: SsmoStepsIdentifiers.UploadMenu,
        ssmoMenuSource: 'FILE_UPLOAD',
      };
    }

    try {
      await patchInboundMerchant(payload, inboundMerchant.merchantId);
      history.push('/onboarding-page');
      return;
    } catch (submitError) {
      return { [FORM_ERROR]: submitError as Error };
    }
  };

  return (
    <Form<ProductList>
      initialValues={initialValues}
      onSubmit={handleSubmitUploadMenu}
      validate={validateYupSchema(productListValidationSchema)}
    >
      {({ handleSubmit, values, hasValidationErrors }) => {
        const isFileSource = values.source === 'file';
        const isDisabled = isFileSource ? uploadedFiles.length === 0 : hasValidationErrors;
        return (
          <StepPageWrapper
            title={formatMessage({
              id: 'self-serve-sign-up.onboarding-page.stages-view.get-started.tasks.share-menu.title',
            })}
            step={SsmoStepsIdentifiers.UploadMenu}
            task={SsmoTask.MENU_SUBMISSION}
            disabled={isDisabled}
          >
            <StyledDescription>
              {formatMessage({ id: 'self-serve-sign-up.wizard.product-list.description' })}
            </StyledDescription>
            <StyledForm id={SsmoStepsIdentifiers.UploadMenu} onSubmit={handleSubmit}>
              <Field<ProductList['source']> name="source">
                {({ input }) => (
                  <RadioGroup>
                    <StyledRadio
                      {...input}
                      checked={values.source === 'file'}
                      onChange={e => {
                        e.preventDefault();
                        input.onChange('file');
                      }}
                    >
                      {formatMessage({ id: 'self-serve-sign-up.wizard.product-list.upload-menu-file' })}
                    </StyledRadio>
                    <StyledRadio
                      {...input}
                      value={input.value?.toString()}
                      checked={values.source === 'link'}
                      onChange={e => {
                        e.preventDefault();
                        input.onChange('link');
                      }}
                    >
                      {formatMessage({ id: 'self-serve-sign-up.wizard.product-list.add-menu-link' })}
                    </StyledRadio>
                  </RadioGroup>
                )}
              </Field>

              {isFileSource ? (
                <>
                  <InputContainer>
                    <DocumentUploader
                      uploadedFiles={uploadedFiles}
                      onChange={handleOnFileUpload}
                      isUploading={isUploadingFile}
                      failedToUpload={failedToUpload}
                      onDeletedFile={handleOnDeleteFile}
                      fileBeingDeletedName={fileBeingDeletedName}
                    />
                  </InputContainer>

                  <Alert
                    title={formatMessage({ id: 'self-serve-sign-up.wizard.product-list.alert-title' })}
                    variant="info"
                  >
                    {formatMessage({ id: 'self-serve-sign-up.wizard.product-list.alert-text' })}
                  </Alert>
                </>
              ) : (
                <Field<ProductList['link']> name="link">
                  {field => (
                    <RFFFieldErrorWrapper field={field}>
                      {input => (
                        <InputContainer
                          label={formatMessage({ id: 'self-serve-sign-up.wizard.product-list.menu-link' })}
                        >
                          <Input
                            {...input}
                            type="text"
                            placeholder="https://"
                            onChange={event => {
                              const processedLink = prefillLinkWithHttps(event.target.value);
                              input.onChange(processedLink);
                            }}
                          />
                        </InputContainer>
                      )}
                    </RFFFieldErrorWrapper>
                  )}
                </Field>
              )}

              <StyledButton type="submit" variant="primary" loading={isUploadingFile} disabled={isDisabled}>
                {formatMessage({ id: 'state.save' })}
              </StyledButton>
            </StyledForm>
          </StepPageWrapper>
        );
      }}
    </Form>
  );
};
export default UploadMenuForm;
